import { useMemo } from 'react';

import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
  ArtificialIntelligence08Icon,
  File01Icon,
  Globe02Icon,
  Mail01Icon,
  MessageProgrammingIcon,
  Pdf01Icon,
  Settings01Icon,
  TimeScheduleIcon,
  UserGroupIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';

import { editorChromeSidebarAsideClassName } from 'components/EditorChromeLayout/EditorChromeLayout';

export const BRANDING_TAB_IDS = [
  'general',
  'email',
  'sms',
  'candidate_experience',
  'lead_generation',
  'clicktrough',
  'documents',
  'reminders',
  'reporting',
];

const BrandingDetailsSidebar = ({
  isCollapsed,
  onToggleCollapsed,
  brand,
  showReporting = false,
  onTabChange,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeItem = BRANDING_TAB_IDS.includes(searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'general';
  const showIconsOnly = isCollapsed ?? false;

  const toggleCollapsed = () => {
    onToggleCollapsed?.(!showIconsOnly);
  };

  const setTab = tabId => {
    if (onTabChange) {
      onTabChange(tabId);
    } else {
      setSearchParams({ tab: tabId });
    }
  };

  const SECTIONS = useMemo(() => {
    return [
      { type: 'item', id: 'general', label: 'General', icon: Settings01Icon },
      { type: 'heading', label: 'Communication' },
      {
        type: 'item',
        id: 'email',
        label: 'Email',
        icon: Mail01Icon,
        active: brand?.use_email_communication,
      },
      {
        type: 'item',
        id: 'sms',
        label: 'SMS',
        icon: MessageProgrammingIcon,
        active: brand?.use_sms_communication,
      },
      {
        type: 'item',
        id: 'candidate_experience',
        label: 'Candidate Experience',
        icon: ArtificialIntelligence08Icon,
        active: brand?.candidate_introduction_text || brand?.volunteer_introduction_text,
      },
      { type: 'heading', label: 'Tracking' },
      {
        type: 'item',
        id: 'lead_generation',
        label: 'Engagement',
        icon: UserGroupIcon,
        active: brand?.follow_up_question_active,
      },
      {
        type: 'item',
        id: 'clicktrough',
        label: 'Clickthrough Websites',
        icon: Globe02Icon,
        active: brand?.click_through_candidate || brand?.click_through_referee,
      },
      { type: 'heading', label: 'Operations' },
      {
        type: 'item',
        id: 'documents',
        label: 'Documents',
        icon: File01Icon,
        active: brand?.documents_active,
      },
      {
        type: 'item',
        id: 'reminders',
        label: 'Reminders',
        icon: TimeScheduleIcon,
        active: brand?.reminders_active,
      },
      ...(showReporting
        ? [
            { type: 'heading', label: 'Output' },
            { type: 'item', id: 'reporting', label: 'PDF Reports', icon: Pdf01Icon },
          ]
        : []),
    ];
  }, [brand, showReporting]);

  return (
    <aside
      id="branding-details-sidebar"
      className={editorChromeSidebarAsideClassName(showIconsOnly)}
      title="Brand settings">
      <section
        className={cn(
          'flex flex-col flex-1 min-h-0 gap-2',
          showIconsOnly && 'gap-1',
          'max-[1140px]:gap-1',
        )}>
        <div className="flex flex-col gap-1 min-h-0 overflow-y-auto flex-grow">
          {SECTIONS.map((entry, idx) => {
            if (entry.type === 'heading') {
              if (showIconsOnly) return null;
              return (
                <p
                  key={`h-${entry.label}-${idx}`}
                  className="text-sm font-semibold uppercase tracking-wide text-neutral-5 px-4 pt-4 pb-1 m-0 max-[1140px]:hidden">
                  {entry.label}
                </p>
              );
            }
            const isActive = activeItem === entry.id;
            return (
              <div
                key={entry.id}
                title={entry.label}
                className={cn(
                  'flex items-center cursor-pointer hover:bg-neutral-3 rounded-2xl p-4 transition-all duration-300',
                  showIconsOnly ? 'justify-center p-3' : 'justify-between',
                  'max-[1140px]:justify-center max-[1140px]:p-3',
                  isActive && 'bg-neutral-3',
                )}
                onClick={() => setTab(entry.id)}>
                <div
                  className={cn(
                    'flex items-center min-w-0',
                    showIconsOnly ? 'gap-0' : 'gap-2 max-[1140px]:gap-0',
                  )}>
                  <div className="relative">
                    <OverlayTrigger
                      trigger="hover"
                      placement="bottom"
                      overlay={
                        <Tooltip>
                          {entry.label}: {entry.active ? 'Active' : 'Inactive'}
                        </Tooltip>
                      }>
                      <HugeiconsIcon
                        icon={entry.icon}
                        size="20"
                        className={cn('shrink-0', isActive ? 'text-neutral-7' : 'text-neutral-6')}
                      />
                    </OverlayTrigger>
                    {entry.active && (
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-2 rounded-full"></div>
                    )}
                  </div>
                  <span
                    className={cn(
                      'text-xl font-regular truncate',
                      showIconsOnly ? 'hidden' : 'max-[1140px]:hidden',
                      isActive ? 'text-neutral-7 font-medium' : 'text-neutral-6',
                    )}>
                    {entry.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className={cn('mt-auto flex pt-4', showIconsOnly ? 'justify-center' : 'justify-end')}>
          <div
            role="button"
            aria-label={showIconsOnly ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!showIconsOnly}
            aria-controls="branding-details-sidebar"
            onClick={toggleCollapsed}
            title={showIconsOnly ? 'Expand sidebar' : 'Collapse sidebar'}
            className={cn(
              'flex items-center bg-neutral-2 border border-neutral-4 shadow-md rounded-2xl p-2 hover:bg-neutral-3 text-neutral-6 hover:text-neutral-7 transition-colors w-max',
              showIconsOnly
                ? 'justify-center p-2'
                : 'justify-start max-[1140px]:justify-center max-[1140px]:p-2',
            )}>
            <HugeiconsIcon
              icon={showIconsOnly ? ArrowRight01Icon : ArrowLeft01Icon}
              size="20"
              strokeWidth="2"
            />
          </div>
        </div>
      </section>
    </aside>
  );
};

export default BrandingDetailsSidebar;
